import api from "../network/api";
import { ApiError, ApiResponse } from "../network/axios";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

// ----------------- Interfaces -----------------
export interface Attendee {
  firstName: string;
  lastName: string;
  email: string;
  dietaryRequirements?: string;
}

export interface CreateCheckoutSessionPayload {
  tier: string;
  quantity: number;
  attendees: Attendee[];
}

export interface CreateCheckoutSessionResponse {
  id: string;
}

export interface OrderDetails {
  _id: string;
  tier: string;
  quantity: number;
  amountTotal: string; // comes from .toFixed(2), so it's a string
  paymentStatus: string;
  createdAt: string; // ISO date string
  tickets: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    dietaryRequirements: string | null;
  }[];
}

export interface Ticket {
  _id: string;
  orderId: OrderDetails;
  attendee: Attendee;
  tier: string;
  createdAt: string;
}

// ----------------- API Calls -----------------
export const orderTicket = async (
  payload: CreateCheckoutSessionPayload
): Promise<ApiResponse<CreateCheckoutSessionResponse>> => {
  const res = await api.post<ApiResponse<CreateCheckoutSessionResponse>>(
    "/tickets/order",
    payload
  );
  return res;
};

export const useOrderTicket = () => {
  return useMutation<
    ApiResponse<CreateCheckoutSessionResponse>,
    ApiError,
    CreateCheckoutSessionPayload
  >({
    mutationFn: orderTicket,
    onSuccess: () => {
      toast("Redirecting to payment...", {
        description: "Your secure checkout will start shortly.",
      });
    },
    onError: (error: ApiError) => {
      toast("Operation Failed", {
        description: error.response?.data?.message || "Something went wrong!",
      });
    },
  });
};

export const getOrderDetails = async (
  sessionId: string
): Promise<ApiResponse<OrderDetails>> => {
  const res = await api.get<ApiResponse<OrderDetails>>(
    `/tickets/order-details?session_id=${sessionId}`
  );
  return res;
};

export const useOrderDetails = (sessionId: string | null) => {
  return useQuery<ApiResponse<OrderDetails>, ApiError>({
    queryKey: ["orderDetails", sessionId],
    queryFn: () => getOrderDetails(sessionId!),
    enabled: !!sessionId, // Only run if sessionId exists
    retry: 2,
    staleTime: 5 * 60 * 1000,
  });
};

export const getTicketById = async (
  ticketId: string
): Promise<ApiResponse<Ticket>> => {
  const res = await api.get<ApiResponse<Ticket>>(
    `/tickets/ticket-details/${ticketId}`
  );
  return res;
};

export const useTicketById = (ticketId: string | null) => {
  return useQuery<ApiResponse<Ticket>, ApiError>({
    queryKey: ["ticket", ticketId],
    queryFn: () => getTicketById(ticketId!),
    enabled: !!ticketId,
    retry: 2,
    staleTime: 5 * 60 * 1000,
  });
};

export const getAllTickets = async (): Promise<ApiResponse<Ticket[]>> => {
  const res = await api.get<ApiResponse<Ticket[]>>(`/tickets`);
  return res;
};

export const useGetAllTickets = () => {
  return useQuery<ApiResponse<Ticket[]>, ApiError>({
    queryKey: ["ticket"],
    queryFn: () => getAllTickets(),
    retry: 2,
    staleTime: 5 * 60 * 1000,
  });
};
